import { useRef, useState } from "react";
import { Box, Typography, Collapse, IconButton } from "@mui/material";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useFloatingBanners } from "@/features/banner/api/useFloatingBanners";
import { isSafeHttpUrl } from "@/utils/bannerUrl";
import { AdSlot } from "@/components/AdSlot";
import { NewsSummaryAPI } from "@/features/mypage/api";
import { News } from "@/features/mypage/components/News";
import { NewsDetailDialog } from "@/features/mypage/components/NewsDetailDialog";

const CARD_GAP = 24;
const PC_MAX = 3;
// PCは1行最大3枚。全幅を3分割して埋める（gap分を差し引く）。1〜2枚は中央寄せ。
const PC_CARD_WIDTH = `calc((100% - ${(PC_MAX - 1) * CARD_GAP}px) / ${PC_MAX})`;

interface MypageTopBannerProps {
  /** ピン留めお知らせ取得の対象年 */
  year: number;
}

/**
 * マイページの「ピックアップ」セクション。月額会員枠(ティール)とは差別化し、
 * 極淡グラデ＋上下境界の器に、見出し→ピン留めお知らせ(最大2件)→広告カードの順で並べる。
 * カード＝画像2:1＋見出し2行＋説明2行＋「詳しく知る→」（高さ統一）。
 * - PC: 最大3枚を横並び（1〜2枚は中央寄せ・カルーセルなし）。
 * - SP: 1枚ずつスナップするカルーセル（手動スワイプ＋ドット・自動送りなし）。
 * 広告0件かつピン留め0件のときはセクションごと非表示。http/https 以外は除外（XSS防止）。
 */
export const MypageTopBanner: React.FC<MypageTopBannerProps> = ({ year }) => {
  const { banners } = useFloatingBanners("mypage_top");
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedNewsId, setSelectedNewsId] = useState<number | null>(null);
  // セクションの開閉状態（既定は開く）。閲覧者ごとの都合なので localStorage に保持。
  const [open, setOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem("mypageTopBannerCollapsed") !== "1";
    } catch {
      return true;
    }
  });
  const toggleOpen = (): void => {
    setOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("mypageTopBannerCollapsed", next ? "0" : "1");
      } catch {
        // localStorage 不可でも動作は継続
      }
      return next;
    });
  };

  // 登録上限は3件（admin側バリデーションで担保）。FEでも防御的に先頭3件までに制限。
  const visibleBanners = banners
    .filter((b) => isSafeHttpUrl(b.imageUrl) && isSafeHttpUrl(b.linkUrl))
    .slice(0, PC_MAX);
  // ピン留めお知らせは最大2件。
  const pinned = NewsSummaryAPI(year).pinned.slice(0, 2);

  if (visibleBanners.length === 0 && pinned.length === 0) return null;

  const handleScroll = (): void => {
    const el = scrollerRef.current;
    if (!el) return;
    setActiveIndex(Math.round(el.scrollLeft / el.clientWidth));
  };

  const goTo = (index: number): void => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: el.clientWidth * index, behavior: "smooth" });
  };

  const showDots = visibleBanners.length > 1;

  return (
    // 月額会員枠(ティール枠ボックス)と差別化: 極淡グラデ＋上下境界の器。
    <Box
      sx={{
        my: { xs: 3, md: 4 },
        px: { xs: 3, md: 5 },
        pt: { xs: 3, md: 4 },
        pb: { xs: 6, md: 7 },
        background: "linear-gradient(180deg, #FBFCFD 0%, #F6F8FA 100%)",
        borderTop: "1px solid #E8ECF0",
        borderBottom: "1px solid #E8ECF0",
        borderRadius: "12px",
      }}
    >
      {/* セクション見出し（下部の「新着情報」と区別するため「ピックアップ」・アクセントはゴールド系）＋開閉トグル */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: open
            ? pinned.length > 0
              ? { xs: 2, md: 2.5 }
              : { xs: 3, md: 4 }
            : 0,
        }}
      >
        <Box
          sx={{ width: 3, height: 20, borderRadius: 2, bgcolor: "#B08D57" }}
        />
        <CampaignOutlinedIcon sx={{ fontSize: 20, color: "#667085" }} />
        <Typography
          sx={{
            fontSize: 17,
            lineHeight: 1.4,
            fontWeight: 700,
            letterSpacing: "0.03em",
            color: "#1F2933",
            flex: 1,
          }}
        >
          ピックアップ
        </Typography>
        <IconButton
          aria-label={open ? "ピックアップを折りたたむ" : "ピックアップを開く"}
          aria-expanded={open}
          onClick={toggleOpen}
          size="small"
          sx={{ color: "#667085" }}
        >
          <ExpandMoreIcon
            sx={{
              transform: open ? "rotate(180deg)" : "none",
              transition: "transform 0.2s ease",
            }}
          />
        </IconButton>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        {/* ピン留めお知らせ（最大2件・0件なら非表示） */}
        {pinned.length > 0 && (
          <Box
            sx={{
              mb: visibleBanners.length > 0 ? { xs: 3, md: 4 } : 0,
              py: { xs: 1.5, md: 2 },
              display: "flex",
              flexDirection: "column",
              gap: 1,
              borderTop: "1px solid #E8ECF0",
              borderBottom: "1px solid #E8ECF0",
            }}
          >
            {pinned.map((news) => (
              <News
                key={news.id}
                {...news}
                variant="compact"
                onClick={() => {
                  setSelectedNewsId(news.id);
                }}
              />
            ))}
          </Box>
        )}

        {/* 広告カード群（0件なら描画しない） */}
        {visibleBanners.length > 0 && (
          <Box
            ref={scrollerRef}
            onScroll={handleScroll}
            sx={{
              display: "flex",
              gap: `${CARD_GAP}px`,
              // PC=最大3枚の横並び（カルーセルなし・1〜2枚は中央寄せ）。SP=横スクロール1枚スナップ。
              flexWrap: { xs: "nowrap", md: "wrap" },
              overflowX: { xs: "auto", md: "visible" },
              justifyContent: { xs: "flex-start", md: "center" },
              scrollSnapType: { xs: "x mandatory", md: "none" },
              // カード高さは見出し/説明の固定行数で揃うため、stretchは不要（余白防止で上揃え）
              alignItems: "flex-start",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {visibleBanners.map((banner) => (
              <Box
                key={banner.id}
                sx={{
                  flex: "0 0 auto",
                  width: { xs: "100%", md: PC_CARD_WIDTH },
                  maxWidth: "100%",
                  scrollSnapAlign: { xs: "center", md: "none" },
                  display: "flex",
                }}
              >
                <AdSlot
                  key={banner.id}
                  banner={banner}
                  position="mypage_top"
                  width="100%"
                  // SPは画像本来の比率で表示（幅基準・クロップなし）。7:3 固定の cover だと
                  // 横長画像が高さ基準で拡大され、高DPRの実機でぼやけるため。
                  // PCは3枚横並びの高さを揃えるため 7:3 を維持。
                  aspectRatio={{ xs: "auto", md: "7 / 3" }}
                  noUpscaleOnMobile
                  showText
                />
              </Box>
            ))}
          </Box>
        )}

        {showDots && (
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              justifyContent: "center",
              gap: 1,
              mt: 2,
            }}
          >
            {visibleBanners.map((banner, i) => (
              <Box
                key={banner.id}
                component="button"
                type="button"
                aria-label={`バナー${i + 1}へ`}
                onClick={() => {
                  goTo(i);
                }}
                sx={{
                  width: 8,
                  height: 8,
                  p: 0,
                  borderRadius: "50%",
                  border: "none",
                  cursor: "pointer",
                  bgcolor: i === activeIndex ? "#555555" : "#CCCCCC",
                }}
              />
            ))}
          </Box>
        )}
      </Collapse>

      <NewsDetailDialog
        newsId={selectedNewsId}
        onClose={() => {
          setSelectedNewsId(null);
        }}
      />
    </Box>
  );
};
